import { useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { acceptInvitation, AuthApiError, getInvitationAcceptance } from '../../auth/authApi'
import type { AuthResponse, InvitationAcceptance } from '../../auth/types'
import { Button, Field, Icon, I } from '../../components/ui'
import { getLocale } from '../../i18n'
import { AuthLayout } from './AuthLayout'
import { passwordMeetsPolicy, passwordPolicyChecks, type PasswordPolicyCheck } from './passwordPolicy'

interface AcceptInvitationPageProps {
  token: string | null
  onAcceptSuccess: (response: AuthResponse) => void
  onBackToLogin: () => void
  onPrivacy: () => void
}

interface InvitationPreviewState {
  token: string | null
  invitation: InvitationAcceptance | null
  invalid: boolean
}

function PasswordChecklist({ checks }: { checks: PasswordPolicyCheck[] }) {
  const { t } = useTranslation()
  return (
    <div style={{ display: 'grid', gap: 7, padding: 12, borderRadius: 12, background: 'var(--ink-50)', border: '1px solid var(--border-subtle)' }}>
      {checks.map(check => (
        <div key={check.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: check.ok ? 'var(--success)' : 'var(--fg-subtle)', fontWeight: check.ok ? 700 : 500 }}>
          <Icon size={14} color={check.ok ? 'var(--success)' : 'var(--fg-disabled)'}>{check.ok ? I.check : I.clock}</Icon>
          {t(`auth.policy.${check.key}`)}
        </div>
      ))}
    </div>
  )
}

function formatDate(value: string, locale: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

function InvalidInvitationState({ onBackToLogin, message }: { onBackToLogin: () => void; message?: string }) {
  const { t } = useTranslation()
  return (
    <AuthLayout
      eyebrow={t('auth.invite.invalidEyebrow')}
      title={t('auth.invite.invalidTitle')}
      subtitle={message ?? t('auth.invite.invalidSubtitle')}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button type="button" onClick={onBackToLogin} style={{ border: 'none', background: 'transparent', color: 'var(--brand-700)', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
            {t('auth.invite.backToLogin')}
          </button>
        </div>
      )}
    >
      <div style={{ display: 'grid', gap: 18 }}>
        <div style={{
          width: 52,
          height: 52,
          borderRadius: 18,
          display: 'grid',
          placeItems: 'center',
          background: 'var(--danger-100)',
          color: 'var(--danger)',
        }}>
          <Icon size={24}>{I.alert}</Icon>
        </div>
        <Button size="lg" onClick={onBackToLogin} style={{ justifyContent: 'center', width: '100%' }}>
          {t('auth.invite.backToLogin')}
        </Button>
      </div>
    </AuthLayout>
  )
}

export function AcceptInvitationPage({ token, onAcceptSuccess, onBackToLogin, onPrivacy }: AcceptInvitationPageProps) {
  const { t, i18n } = useTranslation()
  const [preview, setPreview] = useState<InvitationPreviewState>({ token: null, invitation: null, invalid: false })
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!token) return

    let cancelled = false
    getInvitationAcceptance(token)
      .then(response => {
        if (cancelled) return
        setPreview({ token, invitation: response, invalid: false })
      })
      .catch(caught => {
        if (cancelled) return
        if (caught instanceof AuthApiError && caught.code === 'TEAM_EMAIL_ALREADY_USER') {
          setError(t('auth.invite.errors.emailAlreadyUser'))
          setPreview({ token, invitation: null, invalid: false })
          return
        }
        setPreview({ token, invitation: null, invalid: true })
      })

    return () => {
      cancelled = true
    }
  }, [token, t])

  const previewMatchesToken = preview.token === token
  const loadingPreview = Boolean(token) && !previewMatchesToken
  const invitation = previewMatchesToken ? preview.invitation : null
  const tokenInvalid = previewMatchesToken && preview.invalid

  if (loadingPreview) {
    return (
      <AuthLayout eyebrow={t('auth.invite.eyebrow')} title={t('auth.invite.loadingTitle')} subtitle={t('auth.invite.loadingSubtitle')}>
        <div style={{ padding: '12px 14px', borderRadius: 12, background: 'var(--ink-50)', color: 'var(--fg-muted)', fontSize: 14, fontWeight: 700 }}>
          {t('app.loading.body')}
        </div>
      </AuthLayout>
    )
  }

  if (!token || tokenInvalid || !invitation) {
    return <InvalidInvitationState onBackToLogin={onBackToLogin} message={error ?? undefined} />
  }

  const activeToken = token
  const checks = passwordPolicyChecks(password, {
    fullName: invitation.fullName,
    email: invitation.email,
  })

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)

    if (!password || !confirmPassword) {
      setError(t('auth.invite.errors.missingFields'))
      return
    }
    if (password !== confirmPassword) {
      setError(t('auth.invite.errors.mismatch'))
      return
    }
    if (!passwordMeetsPolicy(checks)) {
      setError(t('auth.invite.errors.policy'))
      return
    }

    setSubmitting(true)
    try {
      const response = await acceptInvitation(activeToken, password, getLocale())
      onAcceptSuccess(response)
    } catch (caught) {
      if (caught instanceof AuthApiError) {
        if (caught.code === 'INVALID_INVITATION') {
          setPreview({ token: activeToken, invitation: null, invalid: true })
        } else if (caught.code === 'TEAM_EMAIL_ALREADY_USER') {
          setError(t('auth.invite.errors.emailAlreadyUser'))
        } else if (caught.code === 'PASSWORD_POLICY_FAILED') {
          setError(t('auth.invite.errors.policyServer'))
        } else {
          setError(t('auth.invite.errors.network'))
        }
      } else {
        setError(t('auth.invite.errors.network'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout
      eyebrow={t('auth.invite.eyebrow')}
      title={t('auth.invite.title', { organization: invitation.organizationName })}
      subtitle={t('auth.invite.subtitle', { organization: invitation.organizationName })}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button type="button" onClick={onBackToLogin} style={{ border: 'none', background: 'transparent', color: 'var(--brand-700)', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
            {t('auth.invite.backToLogin')}
          </button>
        </div>
      )}
    >
      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 15 }}>
        <div style={{ display: 'grid', gap: 8, padding: 12, borderRadius: 12, background: 'var(--brand-100)', border: '1px solid var(--brand-100)' }}>
          <div style={{ fontSize: 13, color: 'var(--fg-muted)' }}>{t('auth.invite.details.organization')}</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--brand-900)' }}>{invitation.organizationName}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>{t('auth.invite.details.invitee')}</div>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--fg)' }}>{invitation.fullName}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>{t('auth.invite.details.email')}</div>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--fg)' }}>{invitation.email}</div>
            </div>
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>
            {t('auth.invite.details.expires', { date: formatDate(invitation.expiresAt, i18n.language) })}
          </div>
        </div>

        <Field label={t('auth.invite.newPassword')} type="password" placeholder={t('auth.invite.newPasswordPlaceholder')} value={password} onChange={event => setPassword(event.target.value)} />
        <Field label={t('auth.invite.confirmPassword')} type="password" placeholder={t('auth.invite.confirmPasswordPlaceholder')} value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} />
        <PasswordChecklist checks={checks} />

        {error && (
          <div style={{ padding: '10px 12px', borderRadius: 10, background: 'var(--danger-100)', color: 'var(--danger)', fontSize: 13, lineHeight: 1.45 }}>
            {error}
          </div>
        )}

        <Button type="submit" size="lg" disabled={submitting} style={{ justifyContent: 'center', width: '100%' }}>
          {submitting ? t('auth.invite.submitting') : t('auth.invite.submit')}
        </Button>
        <p style={{ color: 'var(--fg-subtle)', fontSize: 'var(--fs-12)', lineHeight: 'var(--lh-relaxed)', margin: 0, textAlign: 'center' }}>
          {t('auth.invite.privacyPrefix')}{' '}
          <a href="/privacy" onClick={(event) => { event.preventDefault(); onPrivacy() }} style={{ color: 'var(--brand-700)', fontWeight: 700 }}>{t('auth.invite.privacy')}</a>.
        </p>
      </form>
    </AuthLayout>
  )
}
